import { ContactSupportRequestState } from 'communication/types/ContactSupportRequest';
import { CheckboxInput } from '@instinet-analytics/semantic-ui-components';
import React from 'react';
import { Button } from '@instinet-analytics/semantic-ui-components';
import { ChannelDestination, ChannelInfo } from 'communication/Channel';
import classNames from 'classnames';
import uniq from 'lodash/uniq';
import { StreamInfo, StreamStatusDummyDestination } from './Streams';
import { UnprocessedResponse } from 'communication/StreamState';
import QuickSearch from 'utils/components/QuickSearch/QuickSearch';

export interface StreamsFilter {
    onlyLatestRequests: boolean;
    includeRpcChannels: boolean;
    channelIds: number[];
    destinations: string[];
    type: 'requests' | 'responses' | 'both';
    quickSearch: string;
}

export interface StreamFilterProps {
    filter: StreamsFilter;
    state: ContactSupportRequestState;
    onUpdateFilter: (nextFilter: StreamsFilter) => void;
}

export default function StreamFilters(props: StreamFilterProps) {
    const { filter, state, onUpdateFilter } = props;

    const channels = getChannels(state, filter);

    const unprocessedResponses: UnprocessedResponse[] =
        state.streams?.unprocessedResponses ?? [];
    const unProcessDestinations = uniq(
        unprocessedResponses.map((u) => u.reason)
    );

    const allSanitizedDestinations = uniq([
        StreamStatusDummyDestination,
        ...channels.map((c) => sanitizeDest(c.destination)),
        ...unProcessDestinations,
    ]);

    const haveInactive = !!channels.filter((c) => !c.isActive);

    const setAllChannels = () => {
        onUpdateFilter({ ...filter, channelIds: [] });
    };

    const setAllDestinations = () => {
        onUpdateFilter({ ...filter, destinations: [] });
    };

    const setAllActiveChannels = () => {
        onUpdateFilter({
            ...filter,
            channelIds: channels.filter((c) => c.isActive).map((c) => c.id),
        });
    };

    const toggleChannel = (id: number) => {
        const nextIds =
            filter.channelIds.indexOf(id) > -1
                ? filter.channelIds.filter((c) => c !== id)
                : [...filter.channelIds, id];
        onUpdateFilter({ ...filter, channelIds: nextIds });
    };

    const toggleDestination = (d: string) => {
        const next =
            filter.destinations.indexOf(d) > -1
                ? filter.destinations.filter((c) => c !== d)
                : [...filter.destinations, d];
        onUpdateFilter({ ...filter, destinations: next });
    };

    const toggleOnlyLatestRequests = () => {
        onUpdateFilter({
            ...filter,
            onlyLatestRequests: !filter.onlyLatestRequests,
        });
    };

    const updateQuickSearch = (quickSearch: string) => {
        onUpdateFilter({
            ...filter,
            quickSearch,
        });
    };

    const toggleIncludeRpcChannels = () => {
        onUpdateFilter({
            ...filter,
            includeRpcChannels: !filter.includeRpcChannels,
        });
    };

    const setType = (type: 'requests' | 'responses' | 'both') => {
        onUpdateFilter({
            ...filter,
            type,
        });
    };

    const unknownChannelIds = uniq(
        unprocessedResponses
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .map((c) => (c.message as any).channelId ?? -999)
            .filter((cid) => !channels.find((c) => c.id === cid))
    );

    const channelsForSelectedDestinations = channels.filter(
        (c) =>
            filter.destinations.length === 0 ||
            filter.destinations.find((d) => sanitizeDest(c.destination) === d)
    );

    return (
        <div className="StreamFilters">
            <div className="channels">
                <div className="title">Channels:</div>
                <div>
                    <Button className={'primary'} onClick={setAllChannels}>
                        All
                    </Button>

                    {haveInactive && (
                        <Button
                            className={'primary'}
                            onClick={setAllActiveChannels}
                        >
                            All Active
                        </Button>
                    )}

                    {channelsForSelectedDestinations.map((c) => (
                        <Button
                            key={c.id}
                            className={classNames(
                                'mini',
                                (filter.channelIds.length === 0 ||
                                    filter.channelIds.indexOf(c.id) > -1) &&
                                    'selected',
                                c.isActive ? 'active' : 'inactive'
                            )}
                            onClick={() => toggleChannel(c.id)}
                        >
                            {c.id}
                        </Button>
                    ))}

                    {unknownChannelIds.map((cid) => (
                        <Button
                            key={cid}
                            className={classNames(
                                'mini',
                                (filter.channelIds.length === 0 ||
                                    filter.channelIds.indexOf(cid) > -1) &&
                                    'selected',
                                'inactive'
                            )}
                            onClick={() => toggleChannel(cid)}
                        >
                            {cid}
                        </Button>
                    ))}
                </div>
            </div>
            <div className="destinations">
                <div className="title">Destinations:</div>
                <div>
                    <Button className={'primary'} onClick={setAllDestinations}>
                        All
                    </Button>

                    {allSanitizedDestinations.map((dest) => (
                        <Button
                            key={dest}
                            className={classNames(
                                'mini active',
                                (filter.destinations.length === 0 ||
                                    filter.destinations.indexOf(dest) > -1) &&
                                    'selected'
                            )}
                            onClick={() => toggleDestination(dest)}
                        >
                            {dest}
                        </Button>
                    ))}
                </div>
            </div>
            <div className="type">
                <div className="title">Type:</div>
                <div>
                    <Button
                        className={classNames(
                            'mini active',
                            filter.type === 'both' && 'selected'
                        )}
                        onClick={() => setType('both')}
                    >
                        All
                    </Button>
                    <Button
                        className={classNames(
                            'mini active',
                            filter.type === 'requests' && 'selected'
                        )}
                        onClick={() => setType('requests')}
                    >
                        Requests
                    </Button>
                    <Button
                        className={classNames(
                            'mini active',
                            filter.type === 'responses' && 'selected'
                        )}
                        onClick={() => setType('responses')}
                    >
                        Responses
                    </Button>
                </div>
            </div>
            <div className="checkboxes">
                <CheckboxInput
                    label="Only latest requests"
                    checked={filter.onlyLatestRequests}
                    onToggle={toggleOnlyLatestRequests}
                />
                <CheckboxInput
                    label="Include RPC Channels"
                    checked={filter.includeRpcChannels}
                    onToggle={toggleIncludeRpcChannels}
                />

                <div className="title">Quick Search:</div>
                <QuickSearch
                    filterApplied={false}
                    filteredRowCount={0}
                    searchText={filter.quickSearch}
                    onSearchChange={updateQuickSearch}
                    placeholder={'Enter text'}
                />
            </div>
        </div>
    );
}

function sanitizeDest(dest?: ChannelDestination | string): string {
    return (dest ?? '')
        .replace('stop', '')
        .replace('start', '')
        .replace('Stream', '');
}

export function applyFilter(messages: StreamInfo[], filter: StreamsFilter) {
    const channelsFilter = (m: StreamInfo) =>
        filter.channelIds.length === 0 ||
        filter.channelIds.indexOf(m.channelId ?? 0) > -1;

    const destinationsFilter = (m: StreamInfo) =>
        filter.destinations.length === 0 ||
        (m.destination === StreamStatusDummyDestination &&
            filter.destinations.indexOf(StreamStatusDummyDestination) > -1) ||
        filter.destinations.indexOf(sanitizeDest(m.destination)) > -1;

    const latestOnlyFilter = (m: StreamInfo) =>
        !filter.onlyLatestRequests || m.isLatest;

    const typeFilter = (m: StreamInfo) =>
        filter.type === 'both' ||
        (filter.type === 'requests' && m.type === 'Request') ||
        (filter.type === 'responses' && m.type === 'Response');

    const quickSearchFilter = applyQuickSearchFilter(filter.quickSearch);

    return messages
        .filter(typeFilter)
        .filter(channelsFilter)
        .filter(latestOnlyFilter)
        .filter(quickSearchFilter)
        .filter(destinationsFilter);
}

export function getChannels(
    state: ContactSupportRequestState,
    filter: StreamsFilter
) {
    const allChannels: ChannelInfo[] = state?.streams?.channels ?? [];
    return filter.includeRpcChannels
        ? allChannels
        : allChannels.filter((c) => !isRpcChannel(c));
}

function isRpcChannel(c: ChannelInfo) {
    return (
        c.destination === 'symbolInfo' ||
        c.destination === 'startExpertiseByOrderIdsStream'
    );
}

function applyQuickSearchFilter(value: string): (m: StreamInfo) => boolean {
    const test = value.trim().toLowerCase();

    if (value.trim() === '') {
        return () => true;
    }

    return (m: StreamInfo) =>
        (m.payload &&
            JSON.stringify(m.payload).toLowerCase().indexOf(test) > -1) ||
        m.destination.toLowerCase().indexOf(test) > -1 ||
        (m.messageNumber ?? '').toString().toLowerCase().indexOf(test) > -1;
}
